<template>
  <div>
    <v-list>
      <template v-if="listItems.length > 0">
        <v-hover
          v-slot:default="{ hover }"
          v-for="item in listItems"
          :key="item.id"
        >
          <v-list-item :data-test="item.title" @click="handleClickItem(item)">
            <v-list-item-avatar
              :color="item.icon.bg"
              size="40"
              v-if="item.icon !== 'none'"
            >
              <template v-if="item.avatar">
                <v-img width="40" height="40" :src="item.avatar" />
              </template>
              <template v-else>
                <v-icon :color="item.icon.color" v-if="item.icon">
                  {{ item.icon.name }}
                </v-icon>
                <span class="white--text headline" v-else-if="item.initials">
                  {{ item.initials }}
                </span>
              </template>
            </v-list-item-avatar>

            <v-list-item-content>
              <v-list-item-title v-if="item.title">
                {{ item.title }}
              </v-list-item-title>

              <v-list-item-subtitle
                v-for="subtitle in item.subtitles"
                :key="subtitle"
              >
                {{ subtitle }}
              </v-list-item-subtitle>
            </v-list-item-content>

            <v-list-item-action v-if="item.isHoverAction">
              <v-btn
                @click="handleContext('detail-notification', item)"
                v-if="item.action === 'info'"
                slot="activator"
                v-show="hover || $vuetify.breakpoint.mdAndDown"
                class="table-button-context"
                icon
              >
                <v-icon>mdi-information</v-icon>
              </v-btn>
              <dropdown-menu
                v-else-if="item.action !== 'copy'"
                item-key-text="text"
                itemKeyValue="id"
                :items="item.contextMenu"
                :hover="hover"
                @input="handleContext($event, item)"
              >
                <template v-slot:activator="{ on }">
                  <slot name="activator" :on="on">
                    <v-btn
                      slot="activator"
                      v-show="hover || $vuetify.breakpoint.mdAndDown"
                      v-on="on"
                      class="table-button-context"
                      icon
                    >
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </slot>
                </template>
              </dropdown-menu>
              <v-btn
                @click="copyText(item)"
                v-else
                slot="activator"
                v-show="hover || $vuetify.breakpoint.mdAndDown"
                class="table-button-context"
                icon
              >
                <v-icon>mdi-image-filter-none</v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>
        </v-hover>
      </template>
    </v-list>
    <infinite-loading class="mb-5" @infinite="loadMore">
      <div slot="spinner">
        <v-progress-circular
          class="mt-2"
          size="25"
          width="2"
          indeterminate
          color="primary"
        />
      </div>
      <div slot="no-more" />

      <div slot="no-results"></div>
    </infinite-loading>
  </div>
</template>

<script>
import DropdownMenu from '@/components/DropdownMenu';
import AvatarItem from '@/components/appTable/avatarItem';
import { useLazyQuery, useResult } from '@vue/apollo-composable';
import notificationsScheme from '@/api/notifications/item.graphql';
import { computed, onMounted, ref, watch } from '@vue/composition-api';
import InfiniteLoading from 'vue-infinite-loading';
import { da } from 'date-fns/locale';
import convertDate from '@/utils/convertDate';
import { Wormhole } from 'portal-vue';
import getIconByTags from '@/utils/getIconByTags';

export default {
  name: 'NotificationsList',
  components: { AvatarItem, DropdownMenu, InfiniteLoading },
  props: {
    tabData: {
      type: Object,
      default: () => ({})
    },
    isNestedEntity: {
      type: Boolean,
      default: false
    }
  },
  setup(props, context) {
    const variables = ref({
      orderBy: 'CREATED_AT_DESC',
      filter: {
        user: {
          equalTo: props.tabData.entityId
        },
        deliveryPath: {
          equalTo: 'PixelAdmin'
        }
      }
    });

    const { result, load, fetchMore } = useLazyQuery(
      notificationsScheme.fetchNotificationsDeliveriesWithCursor,
      variables.value,
      {
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-only'
      }
    );

    const notificationsConnections = useResult(
      result,
      {
        edges: [],
        pageInfo: {},
        totalCount: 0
      },
      data => data.notificationDeliveriesConnection
    );

    const loadMore = async state => {
      const result = await fetchMore({
        variables: {
          after: notificationsConnections.value.pageInfo.endCursor
        }
      });

      if (result?.data.notificationDeliveriesConnection.pageInfo.hasNextPage) {
        state?.loaded();
      } else {
        state?.complete();
      }
    };

    const listItems = computed(() =>
      notificationsConnections.value.edges.map(({ node: item }) => {
        return {
          raw: item,
          id: item.id,
          title: item.message,
          subtitles: [convertDate(item.createdAt)],
          isHoverAction: false,
          contextMenu: [],
          actionClickElement: () => {
            import('@/router/routes/notifications/item/index.vue').then(r => {
              const passengers = [
                context.root.$createElement(r.default, {
                  props: {
                    id: item.notification.id,
                    isNestedEntity: true
                  }
                })
              ];
              Wormhole.open({
                to: 'user-portal',
                from: 'user',
                passengers
              });
            });
          },
          icon: getIconByTags(item.notification.tags),
          action: 'select'
        };
      })
    );

    const handleContext = (action, ctx) => {
      const eventPayload = {
        actionName: action,
        context: ctx,
        id: typeof ctx === 'string' ? ctx : ctx.id
      };
      context.emit('action', eventPayload);
    };

    const handleClickItem = item => {
      if (!props.isNestedEntity && item.actionClickElement) {
        item.actionClickElement();
      }
    };

    const copyText = e => {
      context.root.$copyText(e).catch(err => {
        console.error(`Cannot copy: ${err}`);
      });
    };

    watch(
      () => props.tabData.entityId,
      () => {
        load();
      },
      {
        immediate: true
      }
    );
    //
    // onMounted(() => {
    //   load();
    // });

    return {
      loadMore,
      listItems,
      handleContext,
      copyText,
      handleClickItem
    };
  }
};
</script>
